import { accountSpaceIndexResponseSchema, agentExecutionReadinessRouteParamsSchema, agentProfileBindingRouteParamsSchema, catchupResponseSchema, createSpaceBodySchema, createSpaceDiagnosticReportBodySchema, createSpaceDiagnosticReportResponseSchema, createSpaceResponseSchema, createSpaceThreadBodySchema, createSpaceThreadResponseSchema, currentDeviceTargetResponseSchema, currentLocalRuntimeReplyReadinessResponseSchema, deleteSpaceResponseSchema, ensureAgentProfilePrimaryBindingBodySchema, ensureAgentProfilePrimaryBindingResponseSchema, forgetComputerResponseSchema, forgetCurrentComputerResponseSchema, gatewayErrorCodeSchema, gatewayErrorEnvelopeSchema, getActiveSpaceWakeProgressResponseSchema, getAgentExecutionReadinessBatchBodySchema, getAgentExecutionReadinessBatchResponseSchema, getAgentExecutionReadinessResponseSchema, getAgentProfilePrimaryBindingResponseSchema, getLocalRuntimeExpectedRoutesQuerySchema, getLocalRuntimeExpectedRoutesResponseSchema, getModelCatalogResponseSchema, getPrepareReadinessQuerySchema, getPrepareReadinessResponseSchema, getSpaceMessageDetailsResponseSchema, getSpaceStatusResponseSchema, getSpaceThreadResponseSchema, getSpaceUpdatesResponseSchema, getSpaceWakeProgressResponseSchema, getSpaceWakeTraceWaterfallResponseSchema, leaveSpaceResponseSchema, listProfileSpaceHistoryResponseSchema, listSpaceThreadEventsResponseSchema, listSpaceThreadSignalsResponseSchema, listSpaceThreadsResponseSchema, localRuntimeEnrollmentBodySchema, localRuntimeEnrollmentResponseSchema, localRuntimesQuerySchema, localRuntimesResponseSchema, normalizeListSignalsReadQuerySemantics, normalizeSpacePasswordText, notifyAgentProfileRouteCatalogChangedBodySchema, notifyAgentProfileRouteCatalogChangedResponseSchema, notifyAgentProfilesRouteCatalogChangedBodySchema, patchProfileSpaceHistoryStatusResponseSchema, postNormalMessageBodySchema, postNormalMessageResponseSchema, postSpaceMembersBodySchema, postSpaceMembersResponseSchema, postSpaceThreadSignalBodySchema, postSpaceThreadSignalResponseSchema, postSpaceWakeStopBodySchema, postSpaceWakeStopResponseSchema, refreshCurrentLocalAgentCatalogResponseSchema, setCurrentLocalAgentEnabledBodySchema, setCurrentLocalAgentEnabledResponseSchema, setLocalRuntimeDisplayNameBodySchema, setSpaceAccessModeBodySchema, spaceAccessModeStateSchema, spaceBootstrapResponseSchema, spaceIdentityDirectorySchema, spaceMemberDirectorySchema, spaceMembersSnapshotSchema, spaceViewerMembershipResponseSchema, submitRuntimeAgentControllerReportsBodySchema, submitRuntimeAgentControllerReportsResponseSchema, submitRuntimeProviderConversationProofsBodySchema, submitRuntimeProviderConversationProofsResponseSchema, submitRuntimeRegistrationBodySchema, submitRuntimeRegistrationResponseSchema, updateAgentProfilePrimaryBindingAgentControllerPermissionModeBodySchema, updateAgentProfilePrimaryBindingModelSelectionBodySchema, upsertProfileSpaceHistoryResponseSchema, wakeLoopRequestContextSchema, writeCurrentDeviceObservedMetadataBodySchema, writeCurrentDeviceObservedMetadataResponseSchema } from "@wakeloop/schemas";
import { WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID, WAKELOOP_HEADER_AGENT_CONTROLLER_REF, WAKELOOP_HEADER_AUTO_RELAY_DEPTH, WAKELOOP_HEADER_CALLER_PRINCIPAL_ID, WAKELOOP_HEADER_DISPATCH_CHAIN_ID, WAKELOOP_HEADER_FORWARDED_BY, WAKELOOP_HEADER_OWNER_NAME, WAKELOOP_HEADER_OWNER_USER_ID, WAKELOOP_HEADER_PROFILE_HANDLE, WAKELOOP_HEADER_PROFILE_ID, WAKELOOP_HEADER_PROFILE_KIND, WAKELOOP_HEADER_PROFILE_NAME, WAKELOOP_HEADER_REQUEST_ID, WAKELOOP_HEADER_SPACE_PASSWORD, WAKELOOP_HEADER_VISITED_PROFILE_IDS, WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH } from "@wakeloop/gateway-contract";
import { parseEnvelope } from "@wakeloop/protocol";

//#region src/base-url.ts
const TRAILING_SLASHES_REGEX = /\/+$/;
const ROOT_PATH_REGEX = /^\/+$/;
function normalizeApiBaseUrl(baseUrl) {
	const url = new URL(baseUrl);
	if (!ROOT_PATH_REGEX.test(url.pathname)) throw new Error("baseUrl must be origin-only (no path prefix)");
	if (url.search || url.hash) throw new Error("baseUrl must be origin-only (no query or hash)");
	url.pathname = "";
	url.search = "";
	url.hash = "";
	return url.toString().replace(TRAILING_SLASHES_REGEX, "");
}

//#endregion
//#region src/profile-space-history/routes.ts
const PROFILE_SPACE_HISTORY_DEFAULT_LIMIT = 20;
function resolveProfileSpaceHistoryGatewayHost(baseUrl) {
	return new URL(normalizeApiBaseUrl(baseUrl)).host.toLowerCase();
}
function buildProfileSpaceHistoryListPath(input) {
	const url = new URL(buildProfileSpaceHistoryBasePath(input.profileId), "https://wakeloop.local");
	url.searchParams.set("gatewayHost", input.gatewayHost);
	for (const status of input.statuses ?? ["active"]) url.searchParams.append("status", status);
	if (input.pinned !== void 0) url.searchParams.set("pinned", String(input.pinned));
	url.searchParams.set("limit", String(Math.max(1, input.limit ?? PROFILE_SPACE_HISTORY_DEFAULT_LIMIT)));
	return `${url.pathname}${url.search}`;
}
function buildProfileSpaceHistoryUpsertPath(input) {
	return `${buildProfileSpaceHistoryBasePath(input.profileId)}/${encodeURIComponent(input.spaceId)}`;
}
function buildProfileSpaceHistoryStatusPath(input) {
	return `${buildProfileSpaceHistoryUpsertPath(input)}/status`;
}
function buildProfileSpaceHistoryBasePath(profileId) {
	return `/v1/profiles/${encodeURIComponent(profileId)}/spaces`;
}

//#endregion
//#region src/account-space-index/routes.ts
const ACCOUNT_SPACE_INDEX_DEFAULT_LIMIT = 500;
function resolveAccountSpaceIndexGatewayHost(baseUrl) {
	return resolveProfileSpaceHistoryGatewayHost(baseUrl);
}
function buildAccountSpaceIndexPath(input) {
	const url = new URL("/v1/account/spaces", "https://wakeloop.local");
	url.searchParams.set("gatewayHost", input.gatewayHost);
	url.searchParams.set("scope", input.scope);
	url.searchParams.set("selectedProfileId", input.selectedProfileId);
	url.searchParams.set("limit", String(Math.max(1, input.limit ?? ACCOUNT_SPACE_INDEX_DEFAULT_LIMIT)));
	if (input.includeRecentlyDeletedItems === true) url.searchParams.set("includeRecentlyDeletedItems", "true");
	if (input.includePurposes === true) url.searchParams.set("includePurposes", "true");
	return `${url.pathname}${url.search}`;
}

//#endregion
//#region src/account-space-index/api.ts
const createAccountSpaceIndexApi = (client) => {
	return { listAccountSpaces: async (input) => await client.requestJson({
		url: buildAccountSpaceIndexPath(input),
		operation: "account_space_index.list",
		method: "GET",
		requestContext: input.requestContext,
		timeoutMs: input.timeoutMs,
		schema: accountSpaceIndexResponseSchema,
		invalidMessage: "invalid account space index response"
	}) };
};

//#endregion
//#region src/agent-profile-binding/routes.ts
function buildAgentProfilePrimaryBindingRoute(agentProfileId) {
	return `/v1/agent-profiles/${encodeURIComponent(agentProfileId)}/primary-binding`;
}
function buildAgentProfilePrimaryBindingModelSelectionRoute(agentProfileId) {
	return `${buildAgentProfilePrimaryBindingRoute(agentProfileId)}/model-selection`;
}
function buildAgentProfilePrimaryBindingAgentControllerPermissionModeRoute(agentProfileId) {
	return `${buildAgentProfilePrimaryBindingRoute(agentProfileId)}/agent-controller-permission-mode`;
}
function buildAgentProfileRouteCatalogSyncRoute(agentProfileId) {
	return `/v1/agent-profiles/${encodeURIComponent(agentProfileId)}/route-catalog-sync`;
}
function buildAgentProfilesRouteCatalogSyncRoute() {
	return "/v1/agent-profiles/route-catalog-sync";
}
function buildAgentExecutionReadinessRoute(agentProfileId) {
	return `/v1/agent-profiles/${encodeURIComponent(agentProfileId)}/execution-readiness`;
}
function buildAgentExecutionReadinessBatchRoute() {
	return "/v1/agent-profiles/execution-readiness/batch";
}

//#endregion
//#region src/agent-profile-binding/api.ts
const getAgentExecutionReadinessInputSchema = agentExecutionReadinessRouteParamsSchema.extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const getAgentExecutionReadinessBatchInputSchema = getAgentExecutionReadinessBatchBodySchema.extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const ensureAgentProfilePrimaryBindingInputSchema = agentProfileBindingRouteParamsSchema.merge(ensureAgentProfilePrimaryBindingBodySchema).extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const notifyAgentProfileRouteCatalogChangedInputSchema = agentProfileBindingRouteParamsSchema.merge(notifyAgentProfileRouteCatalogChangedBodySchema).extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const notifyAgentProfilesRouteCatalogChangedInputSchema = notifyAgentProfilesRouteCatalogChangedBodySchema.extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const updateAgentProfilePrimaryBindingModelSelectionInputSchema = agentProfileBindingRouteParamsSchema.merge(updateAgentProfilePrimaryBindingModelSelectionBodySchema).extend({ requestContext: wakeLoopRequestContextSchema.optional() });
const updateAgentProfilePrimaryBindingAgentControllerPermissionModeInputSchema = agentProfileBindingRouteParamsSchema.merge(updateAgentProfilePrimaryBindingAgentControllerPermissionModeBodySchema).extend({ requestContext: wakeLoopRequestContextSchema.optional() });
function requireValidBindingCommandBody(input) {
	const parsed = ensureAgentProfilePrimaryBindingInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Profile Binding payload.");
	const { agentProfileId: _agentProfileId, requestContext: _requestContext, ...body } = parsed.data;
	return body;
}
function requireValidRouteCatalogChangedBody(input) {
	const parsed = notifyAgentProfileRouteCatalogChangedInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Profile route catalog notification payload.");
	const { agentProfileId: _agentProfileId, requestContext: _requestContext, ...body } = parsed.data;
	return body;
}
function requireValidRouteCatalogChangedBatchBody(input) {
	const parsed = notifyAgentProfilesRouteCatalogChangedInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Profile route catalog notification payload.");
	const { requestContext: _requestContext, ...body } = parsed.data;
	return body;
}
function requireValidModelSelectionPatchBody(input) {
	const parsed = updateAgentProfilePrimaryBindingModelSelectionInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Profile Binding model selection payload.");
	const { agentProfileId: _agentProfileId, requestContext: _requestContext, ...body } = parsed.data;
	return body;
}
function requireValidAgentControllerPermissionModePatchBody(input) {
	const parsed = updateAgentProfilePrimaryBindingAgentControllerPermissionModeInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Profile Binding Agent Controller Permission Mode payload.");
	const { agentProfileId: _agentProfileId, requestContext: _requestContext, ...body } = parsed.data;
	return body;
}
function requireValidExecutionReadinessInput(input) {
	const parsed = getAgentExecutionReadinessInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Execution Readiness request.");
	return parsed.data;
}
function requireValidExecutionReadinessBatchInput(input) {
	const parsed = getAgentExecutionReadinessBatchInputSchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Agent Execution Readiness batch request.");
	return parsed.data;
}
const createAgentProfileBindingApi = (client) => {
	return {
		getExecutionReadiness: async (input) => {
			const request = requireValidExecutionReadinessInput(input);
			return await client.requestJson({
				invalidMessage: "Invalid Agent Execution Readiness response.",
				method: "GET",
				operation: "agent_execution_readiness.read",
				requestContext: request.requestContext,
				schema: getAgentExecutionReadinessResponseSchema,
				url: buildAgentExecutionReadinessRoute(request.agentProfileId)
			});
		},
		getExecutionReadinessBatch: async (input) => {
			const request = requireValidExecutionReadinessBatchInput(input);
			return await client.requestJson({
				body: JSON.stringify({
					agentProfileIds: request.agentProfileIds,
					...request.targetRuntime === void 0 ? {} : { targetRuntime: request.targetRuntime }
				}),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Execution Readiness batch response.",
				method: "POST",
				operation: "agent_execution_readiness.batch_read",
				requestContext: request.requestContext,
				schema: getAgentExecutionReadinessBatchResponseSchema,
				url: buildAgentExecutionReadinessBatchRoute()
			});
		},
		getPrimaryBinding: async (input) => await client.requestJson({
			invalidMessage: "Invalid Agent Profile Binding response.",
			method: "GET",
			operation: "agent_profile_binding.primary.read",
			requestContext: input.requestContext,
			schema: getAgentProfilePrimaryBindingResponseSchema,
			url: buildAgentProfilePrimaryBindingRoute(input.agentProfileId)
		}),
		ensurePrimaryBinding: async (input) => {
			const body = requireValidBindingCommandBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Profile Binding command response.",
				method: "PUT",
				operation: "agent_profile_binding.primary.ensure",
				requestContext: input.requestContext,
				schema: ensureAgentProfilePrimaryBindingResponseSchema,
				url: buildAgentProfilePrimaryBindingRoute(input.agentProfileId)
			});
		},
		notifyRouteCatalogChanged: async (input) => {
			const body = requireValidRouteCatalogChangedBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Profile route catalog notification response.",
				method: "POST",
				operation: "agent_profile_binding.route_catalog_sync",
				requestContext: input.requestContext,
				schema: notifyAgentProfileRouteCatalogChangedResponseSchema,
				url: buildAgentProfileRouteCatalogSyncRoute(input.agentProfileId)
			});
		},
		notifyRouteCatalogChangedBatch: async (input) => {
			const body = requireValidRouteCatalogChangedBatchBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Profile route catalog notification response.",
				method: "POST",
				operation: "agent_profile_binding.route_catalog_sync.batch",
				requestContext: input.requestContext,
				schema: notifyAgentProfileRouteCatalogChangedResponseSchema,
				url: buildAgentProfilesRouteCatalogSyncRoute()
			});
		},
		updatePrimaryBindingModelSelection: async (input) => {
			const body = requireValidModelSelectionPatchBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Profile Binding model selection response.",
				method: "PATCH",
				operation: "agent_profile_binding.primary.model_selection.update",
				requestContext: input.requestContext,
				schema: ensureAgentProfilePrimaryBindingResponseSchema,
				url: buildAgentProfilePrimaryBindingModelSelectionRoute(input.agentProfileId)
			});
		},
		updatePrimaryBindingAgentControllerPermissionMode: async (input) => {
			const body = requireValidAgentControllerPermissionModePatchBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Agent Profile Binding Agent Controller Permission Mode response.",
				method: "PATCH",
				operation: "agent_profile_binding.primary.agent_controller_permission_mode.update",
				requestContext: input.requestContext,
				schema: ensureAgentProfilePrimaryBindingResponseSchema,
				url: buildAgentProfilePrimaryBindingAgentControllerPermissionModeRoute(input.agentProfileId)
			});
		}
	};
};

//#endregion
//#region src/errors.ts
const FALLBACK_GATEWAY_HTTP_ERROR_CODE = "gateway.http_error";
const INVALID_RESPONSE_ERROR_CODE = "gateway.invalid_response";
const GENERIC_NETWORK_ERROR_CODES = ["network.timeout", "network.request_failed"];
const KNOWN_API_CLIENT_ERROR_CODES = new Set([
	...gatewayErrorCodeSchema.options,
	FALLBACK_GATEWAY_HTTP_ERROR_CODE,
	INVALID_RESPONSE_ERROR_CODE,
	...GENERIC_NETWORK_ERROR_CODES
]);
var WakeLoopApiClientHttpError = class extends Error {
	code;
	requestId;
	status;
	rawBody;
	upstreamCode;
	constructor(input) {
		super(input.message);
		this.name = "WakeLoopApiClientHttpError";
		this.code = input.code;
		this.requestId = normalizeOptionalText(input.requestId);
		this.status = input.status;
		this.rawBody = input.rawBody;
		this.upstreamCode = normalizeOptionalText(input.upstreamCode);
	}
};
var WakeLoopApiClientInvalidResponseError = class extends Error {
	code = INVALID_RESPONSE_ERROR_CODE;
	operation;
	requestId;
	status;
	rawBody;
	schemaIssueMessage;
	schemaIssuePath;
	upstreamCode = null;
	constructor(input) {
		super(input.message);
		this.name = "WakeLoopApiClientInvalidResponseError";
		this.operation = input.operation;
		this.requestId = normalizeOptionalText(input.requestId);
		this.status = input.status;
		this.rawBody = input.rawBody;
		this.schemaIssueMessage = normalizeOptionalText(input.schemaIssue?.message);
		this.schemaIssuePath = formatSchemaIssuePath(input.schemaIssue);
	}
};
function toStructuredApiClientFailure(error) {
	if (error instanceof WakeLoopApiClientHttpError) return {
		code: error.code,
		message: normalizeFailureMessage(error.message),
		requestId: error.requestId,
		status: error.status,
		upstreamCode: error.upstreamCode
	};
	if (error instanceof WakeLoopApiClientInvalidResponseError) return withInvalidResponseDiagnostics({
		code: error.code,
		message: normalizeFailureMessage(error.message),
		requestId: error.requestId,
		status: error.status,
		upstreamCode: null
	}, {
		schemaIssueMessage: error.schemaIssueMessage,
		schemaIssuePath: error.schemaIssuePath
	});
	return withInvalidResponseDiagnostics({
		code: readGenericErrorCode(error),
		message: normalizeFailureMessage(readFailureMessage(error)),
		requestId: readOptionalObjectTextField(error, "requestId"),
		status: readOptionalObjectStatus(error),
		upstreamCode: readOptionalObjectTextField(error, "upstreamCode")
	}, {
		schemaIssueMessage: readOptionalObjectTextField(error, "schemaIssueMessage"),
		schemaIssuePath: readOptionalObjectTextField(error, "schemaIssuePath")
	});
}
function readApiClientTextResponse(input) {
	return input.response.text().then((raw) => {
		assertApiClientResponseOk({
			response: input.response,
			raw
		});
		return raw;
	});
}
async function readApiClientJsonResponse(input) {
	let raw;
	try {
		raw = await input.response.text();
	} catch (error) {
		const bodyReadError = new Error(toErrorMessage$1(error), { cause: error });
		bodyReadError.code = "network.request_failed";
		bodyReadError.status = input.response.status;
		throw bodyReadError;
	}
	assertApiClientResponseOk({
		response: input.response,
		raw
	});
	const parsed = input.schema.safeParse(parseJsonResponse(raw));
	if (!parsed.success) throw new WakeLoopApiClientInvalidResponseError({
		message: input.invalidMessage,
		operation: input.operation,
		requestId: input.response.headers.get(WAKELOOP_HEADER_REQUEST_ID),
		status: input.response.status,
		rawBody: raw,
		schemaIssue: parsed.error?.issues?.[0] ?? null
	});
	return parsed.data;
}
function assertApiClientResponseOk(input) {
	if (input.response.ok) return;
	throw parseApiClientErrorResponse(input);
}
function parseApiClientErrorResponse(input) {
	const parsed = gatewayErrorEnvelopeSchema.safeParse(parseJsonResponse(input.raw));
	const headerRequestId = normalizeOptionalText(input.response.headers.get(WAKELOOP_HEADER_REQUEST_ID));
	if (parsed.success) return new WakeLoopApiClientHttpError({
		code: parsed.data.error.code,
		message: parsed.data.error.message,
		requestId: parsed.data.error.requestId || headerRequestId,
		status: input.response.status,
		rawBody: input.raw,
		upstreamCode: parsed.data.error.upstreamCode ?? null
	});
	return new WakeLoopApiClientHttpError({
		code: FALLBACK_GATEWAY_HTTP_ERROR_CODE,
		message: normalizeOptionalText(input.raw) ?? `Gateway request failed with HTTP ${input.response.status}.`,
		requestId: headerRequestId,
		status: input.response.status,
		rawBody: input.raw,
		upstreamCode: null
	});
}
function parseJsonResponse(raw) {
	if (!raw.trim()) return null;
	try {
		return JSON.parse(raw);
	} catch {
		return null;
	}
}
function normalizeOptionalText(value) {
	const normalized = String(value ?? "").trim();
	return normalized.length > 0 ? normalized : null;
}
function toErrorMessage$1(error) {
	if (error instanceof Error) return normalizeOptionalText(error.message) ?? error.name;
	return normalizeOptionalText(error) ?? "request failed";
}
function formatSchemaIssuePath(issue) {
	const path = issue?.path;
	if (!path || path.length === 0) return null;
	return path.map((part) => String(part)).join(".");
}
function withInvalidResponseDiagnostics(failure, diagnostics) {
	if (!(diagnostics.schemaIssueMessage || diagnostics.schemaIssuePath)) return failure;
	return {
		...failure,
		schemaIssueMessage: diagnostics.schemaIssueMessage,
		schemaIssuePath: diagnostics.schemaIssuePath
	};
}
function normalizeFailureMessage(value) {
	return normalizeOptionalText(value) ?? "Unknown error.";
}
function readFailureMessage(value) {
	if (value instanceof Error) return normalizeOptionalText(value.message);
	if (typeof value === "string") return normalizeOptionalText(value);
	if (value && typeof value === "object" && "message" in value) {
		const message = value.message;
		return typeof message === "string" ? normalizeOptionalText(message) : null;
	}
	if (value === null || value === void 0 || typeof value === "object") return null;
	return normalizeOptionalText(value);
}
function readOptionalObjectTextField(value, fieldName) {
	if (!value || typeof value !== "object" || !(fieldName in value)) return null;
	const fieldValue = value[fieldName];
	return typeof fieldValue === "string" ? normalizeOptionalText(fieldValue) : null;
}
function readOptionalObjectStatus(value) {
	if (!value || typeof value !== "object" || !("status" in value)) return null;
	const status = value.status;
	return typeof status === "number" && Number.isInteger(status) ? status : null;
}
function readGenericErrorCode(value) {
	if (!value || typeof value !== "object" || !("code" in value)) return null;
	const normalized = normalizeOptionalText(value.code ?? null);
	if (!(normalized && KNOWN_API_CLIENT_ERROR_CODES.has(normalized))) return null;
	return normalized;
}

//#endregion
//#region src/client.ts
const RESERVED_HEADERS = new Set([
	"authorization",
	WAKELOOP_HEADER_AGENT_CONTROLLER_CONVERSATION_ID,
	WAKELOOP_HEADER_AUTO_RELAY_DEPTH,
	WAKELOOP_HEADER_CALLER_PRINCIPAL_ID,
	WAKELOOP_HEADER_AGENT_CONTROLLER_REF,
	WAKELOOP_HEADER_DISPATCH_CHAIN_ID,
	WAKELOOP_HEADER_FORWARDED_BY,
	WAKELOOP_HEADER_OWNER_NAME,
	WAKELOOP_HEADER_OWNER_USER_ID,
	WAKELOOP_HEADER_PROFILE_HANDLE,
	WAKELOOP_HEADER_PROFILE_ID,
	WAKELOOP_HEADER_PROFILE_KIND,
	WAKELOOP_HEADER_PROFILE_NAME,
	WAKELOOP_HEADER_REQUEST_ID,
	WAKELOOP_HEADER_SPACE_PASSWORD,
	WAKELOOP_HEADER_VISITED_PROFILE_IDS,
	WAKELOOP_HEADER_VISITED_PROFILE_IDS_HASH
].map((value) => value.toLowerCase()));
function createWakeLoopApiClient(config) {
	const normalizedBaseUrl = normalizeApiBaseUrl(config.baseUrl);
	return {
		requestResponse: async (input) => await performRequest({
			config,
			baseUrl: normalizedBaseUrl,
			input
		}),
		requestJson: async (input) => {
			return await readApiClientJsonResponse({
				response: await performRequest({
					config,
					baseUrl: normalizedBaseUrl,
					input
				}),
				schema: input.schema,
				invalidMessage: input.invalidMessage,
				operation: input.operation
			});
		},
		requestText: async (input) => {
			return await readApiClientTextResponse({ response: await performRequest({
				config,
				baseUrl: normalizedBaseUrl,
				input
			}) });
		}
	};
}
async function performRequest(input) {
	const requestUrl = resolveRequestUrl(input.baseUrl, input.input.url);
	const headers = await resolveRequestHeaders({
		config: input.config,
		requestUrl,
		requestContext: input.input.requestContext,
		spacePassword: input.input.spacePassword,
		requestHeaders: input.input.headers
	});
	return await input.config.transport({
		url: requestUrl,
		operation: input.input.operation,
		timeoutMs: input.input.timeoutMs,
		init: {
			method: input.input.method,
			headers,
			body: input.input.body ?? void 0,
			signal: input.input.signal
		}
	});
}
async function resolveRequestHeaders(input) {
	assertNoReservedHeaderOverrides(input.requestHeaders);
	const authHeaders = normalizeHeaderRecord(input.config.getAuthHeaders ? await input.config.getAuthHeaders({ requestUrl: input.requestUrl }) : {});
	const requestContextHeaders = normalizeHeaderRecord(input.requestContext && input.config.getRequestContextHeaders ? await input.config.getRequestContextHeaders({
		requestUrl: input.requestUrl,
		requestContext: input.requestContext,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword }
	}) : {});
	const requestHeaders = normalizeHeaderRecord(input.requestHeaders ?? {});
	const spacePassword = input.spacePassword === void 0 ? void 0 : normalizeSpacePasswordText(input.spacePassword);
	return {
		...authHeaders,
		...requestContextHeaders,
		...spacePassword ? { [WAKELOOP_HEADER_SPACE_PASSWORD]: spacePassword } : {},
		...requestHeaders
	};
}
function resolveRequestUrl(baseUrl, candidate) {
	return new URL(candidate, `${baseUrl}/`).toString();
}
function assertNoReservedHeaderOverrides(headers) {
	if (!headers) return;
	for (const headerName of Object.keys(headers)) {
		if (!RESERVED_HEADERS.has(headerName.toLowerCase())) continue;
		throw new Error(`request headers must not override reserved header: ${headerName}`);
	}
}
function normalizeHeaderRecord(headers) {
	const normalized = {};
	for (const [key, value] of Object.entries(headers)) {
		const normalizedKey = String(key).trim();
		if (normalizedKey.length === 0) continue;
		normalized[normalizedKey] = String(value);
	}
	return normalized;
}

//#endregion
//#region src/config/routes.ts
function buildModelCatalogPath() {
	return "/v1/config/model-catalog";
}

//#endregion
//#region src/config/api.ts
const createConfigApi = (client) => {
	return { getModelCatalog: () => getModelCatalog(client) };
};
async function getModelCatalog(client) {
	return await client.requestJson({
		url: buildModelCatalogPath(),
		operation: "config.model_catalog.read",
		method: "GET",
		schema: getModelCatalogResponseSchema,
		invalidMessage: "invalid model catalog response"
	});
}

//#endregion
//#region src/current-device/routes.ts
const CURRENT_DEVICE_ROUTE = "/v1/devices/current";
function buildLocalRuntimeEnrollmentRoute(deviceId) {
	return `/v1/local-runtimes/${encodeURIComponent(deviceId)}/enrollment`;
}
const FORGET_CURRENT_COMPUTER_ROUTE = "/v1/devices/current/forget";
const CURRENT_LOCAL_RUNTIME_REPLY_READINESS_ROUTE = "/v1/local-runtimes/current/reply-readiness";
const CURRENT_LOCAL_AGENT_CATALOG_REFRESH_ROUTE = "/v1/local-runtimes/current/local-agents/refresh-catalog";
function buildCurrentLocalAgentEnabledRoute(agentId) {
	return `/v1/local-runtimes/current/local-agents/${encodeURIComponent(agentId)}/enabled`;
}
const LOCAL_RUNTIMES_ROUTE = "/v1/local-runtimes";
function buildLocalRuntimesRoute(input) {
	if (!input?.connectionIdentityAdoption) return LOCAL_RUNTIMES_ROUTE;
	return `${LOCAL_RUNTIMES_ROUTE}?${new URLSearchParams({ connectionIdentityAdoption: input.connectionIdentityAdoption }).toString()}`;
}
function buildForgetComputerRoute(deviceId) {
	return `/v1/devices/${encodeURIComponent(deviceId)}/forget`;
}
function buildCurrentDeviceObservedMetadataRoute(deviceId) {
	return `/v1/devices/${encodeURIComponent(deviceId)}/observed-metadata`;
}
function buildLocalRuntimeDisplayNameRoute(deviceId) {
	return `/v1/local-runtimes/${encodeURIComponent(deviceId)}/display-name`;
}

//#endregion
//#region src/current-device/api.ts
function requireValidLocalRuntimeEnrollmentBody(input) {
	const parsed = localRuntimeEnrollmentBodySchema.safeParse({
		...input.connectedComputerId === void 0 ? {} : { connectedComputerId: input.connectedComputerId },
		runtimeLane: input.runtimeLane
	});
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid local runtime enrollment body.");
	return parsed.data;
}
function requireValidCurrentDeviceObservedMetadataBody(input) {
	const parsed = writeCurrentDeviceObservedMetadataBodySchema.safeParse({ metadata: input.metadata });
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid current device observed metadata body.");
	return parsed.data;
}
function requireValidLocalRuntimesQuery(input = {}) {
	const parsed = localRuntimesQuerySchema.safeParse(input);
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid local runtimes query.");
	return parsed.data;
}
function requireValidLocalRuntimeDisplayNameBody(input) {
	const parsed = setLocalRuntimeDisplayNameBodySchema.safeParse({ displayName: input.displayName });
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid local runtime display name body.");
	return parsed.data;
}
function requireValidCurrentLocalAgentEnabledBody(input) {
	const parsed = setCurrentLocalAgentEnabledBodySchema.safeParse({ enabled: input.enabled });
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid local agent enabled payload.");
	return parsed.data;
}
const createCurrentDeviceApi = (client) => {
	return {
		getCurrentLocalRuntimeReplyReadiness: async () => await client.requestJson({
			url: CURRENT_LOCAL_RUNTIME_REPLY_READINESS_ROUTE,
			operation: "local_runtime.current.reply_readiness.read",
			method: "GET",
			schema: currentLocalRuntimeReplyReadinessResponseSchema,
			invalidMessage: "Invalid current local runtime reply readiness response."
		}),
		getCurrentTarget: async () => await client.requestJson({
			url: CURRENT_DEVICE_ROUTE,
			operation: "current_device.read",
			method: "GET",
			schema: currentDeviceTargetResponseSchema,
			invalidMessage: "Invalid current device target response."
		}),
		forgetComputer: async (input) => await client.requestJson({
			url: buildForgetComputerRoute(input.deviceId),
			operation: "local_runtime.forget",
			method: "POST",
			schema: forgetComputerResponseSchema,
			invalidMessage: "Invalid forgotten computer response."
		}),
		forgetCurrentComputer: async () => await client.requestJson({
			url: FORGET_CURRENT_COMPUTER_ROUTE,
			operation: "current_device.forget",
			method: "POST",
			schema: forgetCurrentComputerResponseSchema,
			invalidMessage: "Invalid forgotten computer response."
		}),
		listLocalRuntimes: async (input) => {
			const query = requireValidLocalRuntimesQuery(input);
			return await client.requestJson({
				url: buildLocalRuntimesRoute(query),
				operation: "local_runtime.list",
				method: "GET",
				schema: localRuntimesResponseSchema,
				invalidMessage: "Invalid local runtimes response."
			});
		},
		enrollLocalRuntime: async (input) => {
			const body = requireValidLocalRuntimeEnrollmentBody(input);
			return await client.requestJson({
				url: buildLocalRuntimeEnrollmentRoute(input.deviceId),
				operation: "local_runtime.enroll",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(body),
				schema: localRuntimeEnrollmentResponseSchema,
				invalidMessage: "Invalid local runtime enrollment response."
			});
		},
		setDisplayName: async (input) => {
			const body = requireValidLocalRuntimeDisplayNameBody(input);
			return await client.requestJson({
				url: buildLocalRuntimeDisplayNameRoute(input.deviceId),
				operation: "local_runtime.display_name.write",
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(body),
				schema: localRuntimesResponseSchema,
				invalidMessage: "Invalid local runtimes response."
			});
		},
		setCurrentLocalAgentEnabled: async (input) => {
			const body = requireValidCurrentLocalAgentEnabledBody(input);
			return await client.requestJson({
				url: buildCurrentLocalAgentEnabledRoute(input.agentId),
				operation: "local_runtime.current.local_agent_enabled.write",
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(body),
				schema: setCurrentLocalAgentEnabledResponseSchema,
				invalidMessage: "Invalid local agent enabled response."
			});
		},
		refreshCurrentLocalAgentCatalog: async () => await client.requestJson({
			url: CURRENT_LOCAL_AGENT_CATALOG_REFRESH_ROUTE,
			operation: "local_runtime.current.local_agent_catalog.refresh",
			method: "POST",
			schema: refreshCurrentLocalAgentCatalogResponseSchema,
			invalidMessage: "Invalid local agent catalog refresh response."
		}),
		writeObservedMetadata: async (input) => {
			const body = requireValidCurrentDeviceObservedMetadataBody(input);
			return await client.requestJson({
				url: buildCurrentDeviceObservedMetadataRoute(input.deviceId),
				operation: "current_device.observed_metadata.write",
				method: "PUT",
				headers: { "content-type": "application/json" },
				body: JSON.stringify(body),
				schema: writeCurrentDeviceObservedMetadataResponseSchema,
				invalidMessage: "Invalid current device observed metadata response."
			});
		}
	};
};

//#endregion
//#region src/local-runtime/api.ts
const LOCAL_RUNTIME_EXPECTED_ROUTES_PATH = "/v1/local-runtime/expected-routes";
function buildExpectedRoutesUrl(input) {
	const parsed = getLocalRuntimeExpectedRoutesQuerySchema.safeParse({
		deviceId: input.deviceId,
		lane: input.lane
	});
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid local runtime expected-routes query.");
	const searchParams = new URLSearchParams();
	searchParams.set("deviceId", parsed.data.deviceId);
	searchParams.set("lane", parsed.data.lane);
	return `${LOCAL_RUNTIME_EXPECTED_ROUTES_PATH}?${searchParams.toString()}`;
}
function createLocalRuntimeExpectedRoutesApi(client) {
	return { getExpectedRoutes: async (input) => await client.requestJson({
		invalidMessage: "Invalid local runtime expected-routes response.",
		method: "GET",
		operation: "local_runtime.expected_routes.read",
		requestContext: input.requestContext,
		schema: getLocalRuntimeExpectedRoutesResponseSchema,
		url: buildExpectedRoutesUrl(input)
	}) };
}

//#endregion
//#region src/prepare-readiness/routes.ts
function buildPrepareReadinessRoute(deviceId) {
	return `/v1/devices/${encodeURIComponent(deviceId)}/prepare-readiness`;
}

//#endregion
//#region src/prepare-readiness/api.ts
function requireValidPrepareReadinessQuery(input) {
	const parsed = getPrepareReadinessQuerySchema.safeParse({
		agentControllerRefs: input.agentControllerRefs,
		lane: input.lane,
		wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId
	});
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Prepare readiness read query.");
	return parsed.data;
}
function buildPrepareReadinessUrl(input) {
	const query = requireValidPrepareReadinessQuery(input);
	const searchParams = new URLSearchParams();
	searchParams.set("lane", query.lane);
	if (query.wakeLoopServiceRuntimeId) searchParams.set("wakeLoopServiceRuntimeId", query.wakeLoopServiceRuntimeId);
	for (const agentControllerRef of query.agentControllerRefs ?? []) searchParams.append("agentControllerRef", agentControllerRef);
	return `${buildPrepareReadinessRoute(input.deviceId)}?${searchParams.toString()}`;
}
const createPrepareReadinessApi = (client) => {
	return { getReadiness: async (input) => await client.requestJson({
		url: buildPrepareReadinessUrl(input),
		operation: "prepare_readiness.read",
		method: "GET",
		requestContext: input.requestContext,
		schema: getPrepareReadinessResponseSchema,
		invalidMessage: "Invalid Prepare readiness response."
	}) };
};

//#endregion
//#region src/profile-space-history/api.ts
const createProfileSpaceHistoryApi = (client) => {
	return {
		listProfileSpaceHistory: async (input) => {
			return (await client.requestJson({
				url: buildProfileSpaceHistoryListPath(input),
				operation: "profile_space_history.list",
				method: "GET",
				requestContext: input.requestContext,
				timeoutMs: input.timeoutMs,
				schema: listProfileSpaceHistoryResponseSchema,
				invalidMessage: "invalid profile-space-history list response"
			})).items;
		},
		upsertProfileSpaceHistory: async (input) => await client.requestJson({
			url: buildProfileSpaceHistoryUpsertPath(input),
			operation: "profile_space_history.upsert",
			method: "PATCH",
			headers: { "content-type": "application/json" },
			body: JSON.stringify(input.body),
			requestContext: input.requestContext,
			timeoutMs: input.timeoutMs,
			schema: upsertProfileSpaceHistoryResponseSchema,
			invalidMessage: "invalid profile-space-history upsert response"
		}),
		updateProfileSpaceHistoryStatus: async (input) => await client.requestJson({
			url: buildProfileSpaceHistoryStatusPath(input),
			operation: "profile_space_history.status.update",
			method: "PATCH",
			headers: { "content-type": "application/json" },
			body: JSON.stringify(input.body),
			requestContext: input.requestContext,
			timeoutMs: input.timeoutMs,
			schema: patchProfileSpaceHistoryStatusResponseSchema,
			invalidMessage: "invalid profile-space-history status response"
		})
	};
};

//#endregion
//#region src/public-space-urls.ts
function publicSpaceUrls(baseUrl) {
	const normalizedBaseUrl = normalizeApiBaseUrl(baseUrl);
	return {
		status: (spaceId) => buildPublicSpaceUrl(normalizedBaseUrl, spaceId, "status"),
		members: (spaceId) => buildPublicSpaceUrl(normalizedBaseUrl, spaceId, "members"),
		signals: (spaceId) => buildPublicSpaceUrl(normalizedBaseUrl, spaceId, "signals")
	};
}
function buildPublicSpaceUrl(baseUrl, spaceId, segment) {
	const url = new URL(baseUrl);
	url.pathname = `/v1/spaces/${encodeURIComponent(spaceId)}/${segment}`;
	return url.toString();
}

//#endregion
//#region src/runtime-reporting/routes.ts
const buildRuntimeRegistrationRoute = (deviceId) => `/v1/devices/${encodeURIComponent(deviceId)}/runtime-registration`;
const buildRuntimeAgentControllerReportsRoute = (deviceId) => `/v1/devices/${encodeURIComponent(deviceId)}/runtime-agent-controller-reports/current`;
const buildRuntimeProviderConversationProofsRoute = (deviceId) => `/v1/devices/${encodeURIComponent(deviceId)}/runtime-provider-conversation-proofs/current`;

//#endregion
//#region src/runtime-reporting/api.ts
function requireValidRuntimeRegistrationBody(input) {
	const parsed = submitRuntimeRegistrationBodySchema.safeParse({ lane: input.lane });
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Runtime registration payload.");
	return parsed.data;
}
function requireValidRuntimeAgentControllerReportsBody(input) {
	const parsed = submitRuntimeAgentControllerReportsBodySchema.safeParse({
		lane: input.lane,
		reports: input.reports,
		wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId
	});
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Runtime Agent Controller Report payload.");
	return parsed.data;
}
function requireValidRuntimeProviderConversationProofsBody(input) {
	const parsed = submitRuntimeProviderConversationProofsBodySchema.safeParse({
		lane: input.lane,
		proofs: input.proofs,
		wakeLoopServiceRuntimeId: input.wakeLoopServiceRuntimeId
	});
	if (!parsed.success) throw new Error(parsed.error.issues[0]?.message ?? "Invalid Provider Conversation Proof payload.");
	return parsed.data;
}
const createRuntimeReportingApi = (client) => {
	return {
		submitRegistration: async (input) => {
			const body = requireValidRuntimeRegistrationBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Runtime registration response.",
				method: "PUT",
				operation: "runtime.registration.submit",
				schema: submitRuntimeRegistrationResponseSchema,
				url: buildRuntimeRegistrationRoute(input.deviceId)
			});
		},
		submitAgentControllerReports: async (input) => {
			const body = requireValidRuntimeAgentControllerReportsBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Runtime Agent Controller Report response.",
				method: "PUT",
				operation: "runtime.agent_controller_reports.submit",
				schema: submitRuntimeAgentControllerReportsResponseSchema,
				url: buildRuntimeAgentControllerReportsRoute(input.deviceId)
			});
		},
		submitProviderConversationProofs: async (input) => {
			const body = requireValidRuntimeProviderConversationProofsBody(input);
			return await client.requestJson({
				body: JSON.stringify(body),
				headers: { "content-type": "application/json" },
				invalidMessage: "Invalid Provider Conversation Proof response.",
				method: "PUT",
				operation: "runtime.provider_conversation_proofs.submit",
				schema: submitRuntimeProviderConversationProofsResponseSchema,
				url: buildRuntimeProviderConversationProofsRoute(input.deviceId)
			});
		}
	};
};

//#endregion
//#region src/space/routes.ts
function buildCreateSpacePath() {
	return "/v1/spaces";
}
function buildSpaceDeletePath(spaceId) {
	return `/v1/spaces/${encodeURIComponent(spaceId)}`;
}
function buildSpaceStatusPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "status");
}
function buildSpaceAccessModePath(spaceId) {
	return buildSpaceRoutePath(spaceId, "access-mode");
}
function buildSpaceBootstrapPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "bootstrap");
}
function buildSpaceMembersPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "members");
}
function buildSpaceMemberDirectoryPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "member-directory");
}
function buildSpaceIdentityDirectoryPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "identity-directory");
}
function buildSpaceViewerMembershipPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "viewer-membership");
}
function buildSpaceLeavePath(spaceId) {
	return buildSpaceRoutePath(spaceId, "leave");
}
function buildSpaceSignalsPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "signals");
}
function buildSpaceUpdatesPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "updates");
}
function buildSpaceWakeProgressPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "wake-progress");
}
function buildActiveSpaceWakeProgressPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "active-wake-progress");
}
function buildSpaceWakeStopPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "wake-stop");
}
function buildSpaceWakeTraceWaterfallPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "wake-trace-waterfall");
}
function buildSpaceMessageDetailsPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "message-details");
}
function buildSpaceDiagnosticReportsPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "diagnostic-reports");
}
function buildSpaceThreadsPath(spaceId) {
	return buildSpaceRoutePath(spaceId, "threads");
}
function buildSpaceThreadPath(spaceId, threadId) {
	return `${buildSpaceThreadsPath(spaceId)}/${encodeURIComponent(threadId)}`;
}
function buildSpaceThreadEventsPath(spaceId, threadId) {
	return `${buildSpaceThreadPath(spaceId, threadId)}/events`;
}
function buildSpaceThreadSignalsPath(spaceId, threadId) {
	return `${buildSpaceThreadPath(spaceId, threadId)}/signals`;
}
function buildSpaceRoutePath(spaceId, segment) {
	return `/v1/spaces/${encodeURIComponent(spaceId)}/${segment}`;
}

//#endregion
//#region src/space/access-mode.ts
async function setSpaceAccessMode(client, input) {
	const parsedBody = setSpaceAccessModeBodySchema.safeParse({
		accessMode: input.accessMode,
		...input.password === void 0 ? {} : { password: input.password }
	});
	if (!parsedBody.success) throw new Error(parsedBody.error.issues[0]?.message ?? "invalid space access mode request body");
	return await client.requestJson({
		url: buildSpaceAccessModePath(input.spaceId),
		operation: "space.access_mode.set",
		method: "PUT",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		schema: spaceAccessModeStateSchema,
		invalidMessage: "invalid space access mode response"
	});
}

//#endregion
//#region src/space/add-members.ts
async function addSpaceMembers(client, input) {
	const parsedBody = postSpaceMembersBodySchema.safeParse({ memberProfileIds: input.memberProfileIds });
	if (!parsedBody.success) throw new Error(parsedBody.error.issues[0]?.message ?? "invalid add space members request body");
	return await client.requestJson({
		url: buildSpaceMembersPath(input.spaceId),
		operation: "space.members.add",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: postSpaceMembersResponseSchema,
		invalidMessage: "invalid add space members response"
	});
}

//#endregion
//#region src/space/signals.ts
const SPACE_HISTORY_SIGNAL_KIND_TO_TYPE = {
	text: "signal.text",
	status: "signal.status",
	system_notice: "signal.system_notice"
};
async function listSpaceSignals(client, input) {
	return await client.requestJson({
		url: buildSpaceSignalsReadPath(input.spaceId, input.query),
		operation: "space.signals.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: catchupResponseSchema,
		invalidMessage: "Unexpected response: expected { signals, nextCursor }"
	});
}
async function listSpaceSignalEnvelopes(client, input) {
	const result = await listSpaceSignals(client, input);
	return {
		envelopes: result.signals.flatMap((item) => {
			const envelope = parseEnvelope(item);
			return envelope ? [envelope] : [];
		}),
		nextCursor: result.nextCursor
	};
}
function buildSpaceSignalsReadPath(spaceId, query) {
	const url = new URL(buildSpaceSignalsPath(spaceId), "https://wakeloop.local");
	for (const [key, value] of buildSpaceSignalsReadSearchParams(query)) url.searchParams.append(key, value);
	return `${url.pathname}${url.search}`;
}
function buildSpaceSignalsReadSearchParams(query) {
	const searchParams = new URLSearchParams();
	const normalizedQuery = normalizeSpaceSignalsReadQuery(query);
	if (normalizedQuery.recent) searchParams.set("recent", "1");
	if (normalizedQuery.includeLimit) searchParams.set("limit", String(normalizedQuery.limit));
	if (normalizedQuery.afterSignalId) searchParams.set("afterSignalId", normalizedQuery.afterSignalId);
	if (normalizedQuery.aroundSignalId) searchParams.set("aroundSignalId", normalizedQuery.aroundSignalId);
	if (normalizedQuery.beforeCount !== null) searchParams.set("beforeCount", String(normalizedQuery.beforeCount));
	if (normalizedQuery.afterCount !== null) searchParams.set("afterCount", String(normalizedQuery.afterCount));
	for (const profileId of normalizedQuery.fromProfileIds) searchParams.append("fromProfileId", profileId);
	for (const kind of normalizedQuery.kinds) searchParams.append("signalType", SPACE_HISTORY_SIGNAL_KIND_TO_TYPE[kind]);
	return searchParams;
}
function normalizeSpaceSignalsReadQuery(query) {
	const afterSignalId = normalizeOptionalString(query?.afterSignalId);
	const aroundSignalId = normalizeOptionalString(query?.aroundSignalId);
	const beforeCount = normalizeOptionalNonNegativeInteger(query?.beforeCount);
	const afterCount = normalizeOptionalNonNegativeInteger(query?.afterCount);
	const limit = normalizeOptionalNonNegativeInteger(query?.limit);
	const recent = query?.recent === true;
	const fromProfileIds = normalizeUniqueStrings(query?.fromProfileIds ?? []);
	const kinds = normalizeKinds(query?.kinds ?? []);
	const semantics = normalizeListSignalsReadQuerySemantics({
		recentOnly: recent,
		hasExplicitLimit: query?.limit !== void 0,
		limit,
		afterSignalId,
		aroundSignalId,
		beforeCount,
		afterCount
	});
	return {
		recent: semantics.recentOnly,
		includeLimit: semantics.includeLimit,
		limit: semantics.limit,
		afterSignalId: semantics.afterSignalId,
		aroundSignalId: semantics.aroundSignalId,
		beforeCount: semantics.beforeCount,
		afterCount: semantics.afterCount,
		fromProfileIds,
		kinds
	};
}
function normalizeOptionalString(value) {
	if (typeof value !== "string") return null;
	const normalized = value.trim();
	return normalized.length > 0 ? normalized : null;
}
function normalizeOptionalNonNegativeInteger(value) {
	if (value === void 0) return null;
	if (!Number.isSafeInteger(value) || value < 0) throw new Error("history query counts must be non-negative integers");
	return value;
}
function normalizeUniqueStrings(values) {
	const deduped = /* @__PURE__ */ new Set();
	for (const value of values) {
		const normalized = normalizeOptionalString(value);
		if (normalized) deduped.add(normalized);
	}
	return [...deduped];
}
function normalizeKinds(values) {
	const deduped = /* @__PURE__ */ new Set();
	for (const value of values) deduped.add(value);
	return [...deduped];
}

//#endregion
//#region src/space/bootstrap.ts
async function getSpaceBootstrap(client, input) {
	return await client.requestJson({
		url: buildSpaceBootstrapReadPath(input.spaceId, input.historyQuery),
		operation: "space.bootstrap.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: spaceBootstrapResponseSchema,
		invalidMessage: "invalid space bootstrap response"
	});
}
function buildSpaceBootstrapReadPath(spaceId, historyQuery) {
	const url = new URL(buildSpaceBootstrapPath(spaceId), "https://wakeloop.local");
	for (const [key, value] of buildSpaceSignalsReadSearchParams(historyQuery)) url.searchParams.append(key, value);
	return `${url.pathname}${url.search}`;
}

//#endregion
//#region src/space/create.ts
async function createSpace(client, input) {
	const body = buildCreateSpaceBody(input);
	const parsedBody = createSpaceBodySchema.safeParse(body);
	if (!parsedBody.success) throw new Error(parsedBody.error.issues[0]?.message ?? "invalid create space request body");
	return await client.requestJson({
		url: buildCreateSpacePath(),
		operation: "space.create",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		schema: createSpaceResponseSchema,
		invalidMessage: "invalid create space response"
	});
}
function buildCreateSpaceBody(input) {
	return {
		...input.name === void 0 ? {} : { name: input.name },
		...input.purpose === void 0 ? {} : { purpose: input.purpose },
		...input.guide === void 0 ? {} : { guide: input.guide },
		...input.accessMode === void 0 ? {} : { accessMode: input.accessMode },
		...input.password === void 0 ? {} : { password: input.password }
	};
}

//#endregion
//#region src/space/delete.ts
async function deleteSpace(client, input) {
	return await client.requestJson({
		url: buildSpaceDeletePath(input.spaceId),
		operation: "space.delete",
		method: "DELETE",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: deleteSpaceResponseSchema,
		invalidMessage: "invalid delete space response"
	});
}

//#endregion
//#region src/space/diagnostic-report.ts
async function createSpaceDiagnosticReport(client, input) {
	const parsedBody = createSpaceDiagnosticReportBodySchema.safeParse(input.body);
	if (!parsedBody.success) {
		const firstIssue = parsedBody.error.issues[0];
		throw new Error(firstIssue?.message ?? "invalid diagnostic report request body");
	}
	return await client.requestJson({
		url: buildSpaceDiagnosticReportsPath(input.spaceId),
		operation: "space.diagnostic_report.create",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: createSpaceDiagnosticReportResponseSchema,
		invalidMessage: "invalid diagnostic report response"
	});
}

//#endregion
//#region src/space/leave.ts
async function leaveSpace(client, input) {
	return await client.requestJson({
		url: buildSpaceLeavePath(input.spaceId),
		operation: "space.leave",
		method: "POST",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: leaveSpaceResponseSchema,
		invalidMessage: "invalid leave space response"
	});
}

//#endregion
//#region src/space/members.ts
async function getSpaceMembersSnapshot(client, input) {
	return await client.requestJson({
		url: buildSpaceMembersPath(input.spaceId),
		operation: "space.members.list",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: spaceMembersSnapshotSchema,
		invalidMessage: "invalid space members response"
	});
}
async function getSpaceMemberDirectory(client, input) {
	return await client.requestJson({
		url: buildSpaceMemberDirectoryPath(input.spaceId),
		operation: "space.member_directory.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: spaceMemberDirectorySchema,
		invalidMessage: "invalid space member directory response"
	});
}
async function getSpaceIdentityDirectory(client, input) {
	return await client.requestJson({
		url: buildSpaceIdentityDirectoryPath(input.spaceId),
		operation: "space.identity_directory.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: spaceIdentityDirectorySchema,
		invalidMessage: "invalid space identity directory response"
	});
}
async function getSpaceViewerMembership(client, input) {
	return await client.requestJson({
		url: buildSpaceViewerMembershipPath(input.spaceId),
		operation: "space.viewer_membership.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: spaceViewerMembershipResponseSchema,
		invalidMessage: "invalid space viewer membership response"
	});
}

//#endregion
//#region src/space/message-details.ts
async function getSpaceMessageDetails(client, input) {
	const searchParams = new URLSearchParams();
	searchParams.set("signalId", input.signalId);
	return await client.requestJson({
		url: `${buildSpaceMessageDetailsPath(input.spaceId)}?${searchParams.toString()}`,
		operation: "space.message_details",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceMessageDetailsResponseSchema,
		invalidMessage: "invalid message details response"
	});
}

//#endregion
//#region src/space/post-normal-message.ts
async function postSpaceNormalMessage(client, input) {
	const body = buildPostNormalMessageBody(input);
	const parsedBody = postNormalMessageBodySchema.safeParse(body);
	if (!parsedBody.success) {
		const firstIssue = parsedBody.error.issues[0];
		throw new Error(firstIssue?.message ?? "invalid normal message request body");
	}
	return await client.requestJson({
		url: buildSpaceSignalsPath(input.spaceId),
		operation: "space.send",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: postNormalMessageResponseSchema,
		invalidMessage: "invalid send response"
	});
}
function buildPostNormalMessageBody(input) {
	return {
		text: input.text,
		...input.messageEnvelope ? { messageEnvelope: input.messageEnvelope } : {},
		...input.replyTo ? { replyTo: input.replyTo } : {},
		...input.meta ? { meta: input.meta } : {}
	};
}

//#endregion
//#region src/space/status.ts
async function getSpaceStatus(client, input) {
	return await client.requestJson({
		url: buildSpaceStatusPath(input.spaceId),
		operation: "space.status.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceStatusResponseSchema,
		invalidMessage: "invalid space status response"
	});
}

//#endregion
//#region src/space/threads.ts
async function createSpaceThread(client, input) {
	const body = {
		rootSignalId: input.rootSignalId,
		clientMutationId: input.clientMutationId
	};
	const parsedBody = createSpaceThreadBodySchema.safeParse(body);
	if (!parsedBody.success) throw new Error(parsedBody.error.issues[0]?.message ?? "Invalid thread request");
	return await client.requestJson({
		url: buildSpaceThreadsPath(input.spaceId),
		operation: "space.thread.create",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: createSpaceThreadResponseSchema,
		invalidMessage: "invalid thread create response"
	});
}
async function listSpaceThreads(client, input) {
	return await client.requestJson({
		url: buildSpaceThreadsPath(input.spaceId),
		operation: "space.thread.list",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: listSpaceThreadsResponseSchema,
		invalidMessage: "invalid thread list response"
	});
}
async function getSpaceThread(client, input) {
	return await client.requestJson({
		url: buildSpaceThreadPath(input.spaceId, input.threadId),
		operation: "space.thread.get",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceThreadResponseSchema,
		invalidMessage: "invalid thread detail response"
	});
}
async function listSpaceThreadEvents(client, input) {
	return await client.requestJson({
		url: buildSpaceThreadEventsPath(input.spaceId, input.threadId),
		operation: "space.thread.events",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: listSpaceThreadEventsResponseSchema,
		invalidMessage: "invalid thread events response"
	});
}
async function listSpaceThreadSignals(client, input) {
	return await client.requestJson({
		url: buildSpaceThreadSignalsPath(input.spaceId, input.threadId),
		operation: "space.thread.signals",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: listSpaceThreadSignalsResponseSchema,
		invalidMessage: "invalid thread signals response"
	});
}
async function postSpaceThreadSignal(client, input) {
	const parsedBody = postSpaceThreadSignalBodySchema.safeParse(input.body);
	if (!parsedBody.success) throw new Error(parsedBody.error.issues[0]?.message ?? "Invalid thread message");
	return await client.requestJson({
		url: buildSpaceThreadSignalsPath(input.spaceId, input.threadId),
		operation: "space.thread.send",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: postSpaceThreadSignalResponseSchema,
		invalidMessage: "invalid thread message response"
	});
}

//#endregion
//#region src/space/updates.ts
async function getSpaceUpdates(client, input) {
	return await client.requestJson({
		url: buildSpaceUpdatesReadPath({
			afterSignalId: input.afterSignalId,
			limit: input.limit,
			spaceId: input.spaceId
		}),
		operation: "space.updates.read",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceUpdatesResponseSchema,
		invalidMessage: "Unexpected response: expected { signals }"
	});
}
function buildSpaceUpdatesReadPath(input) {
	const afterSignalId = input.afterSignalId.trim();
	if (afterSignalId.length === 0) throw new Error("afterSignalId is required");
	if (!Number.isSafeInteger(input.limit) || input.limit <= 0) throw new Error("limit must be a positive integer");
	const url = new URL(buildSpaceUpdatesPath(input.spaceId), "https://wakeloop.local");
	url.searchParams.set("afterSignalId", afterSignalId);
	url.searchParams.set("limit", String(input.limit));
	return `${url.pathname}${url.search}`;
}

//#endregion
//#region src/space/wake-progress.ts
async function getSpaceWakeProgress(client, input) {
	const searchParams = new URLSearchParams();
	searchParams.set("clientMessageId", input.clientMessageId);
	if (input.includeWakeStopEligibility === true) searchParams.set("includeWakeStopEligibility", "true");
	if (input.targetProfileId) searchParams.set("targetProfileId", input.targetProfileId);
	const query = searchParams.toString();
	return await client.requestJson({
		url: `${buildSpaceWakeProgressPath(input.spaceId)}?${query}`,
		operation: "space.wake_progress",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceWakeProgressResponseSchema,
		invalidMessage: "invalid wake progress response"
	});
}
async function getActiveSpaceWakeProgress(client, input) {
	const searchParams = new URLSearchParams();
	if (input.includeWakeStopEligibility === true) searchParams.set("includeWakeStopEligibility", "true");
	if (input.targetProfileId) searchParams.set("targetProfileId", input.targetProfileId);
	const query = searchParams.toString();
	return await client.requestJson({
		url: query ? `${buildActiveSpaceWakeProgressPath(input.spaceId)}?${query}` : buildActiveSpaceWakeProgressPath(input.spaceId),
		operation: "space.active_wake_progress",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getActiveSpaceWakeProgressResponseSchema,
		invalidMessage: "invalid active wake progress response"
	});
}

//#endregion
//#region src/space/wake-stop.ts
async function postSpaceWakeStop(client, input) {
	const parsedBody = postSpaceWakeStopBodySchema.safeParse(input.body);
	if (!parsedBody.success) {
		const firstIssue = parsedBody.error.issues[0];
		throw new Error(firstIssue?.message ?? "invalid Wake Stop request body");
	}
	return await client.requestJson({
		url: buildSpaceWakeStopPath(input.spaceId),
		operation: "space.wake_stop",
		method: "POST",
		headers: { "content-type": "application/json" },
		body: JSON.stringify(parsedBody.data),
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: postSpaceWakeStopResponseSchema,
		invalidMessage: "invalid Wake Stop response"
	});
}

//#endregion
//#region src/space/wake-trace-waterfall.ts
async function getSpaceWakeTraceWaterfall(client, input) {
	const searchParams = new URLSearchParams();
	if (input.clientMessageId) searchParams.set("clientMessageId", input.clientMessageId);
	if (input.sourceSignalId) searchParams.set("sourceSignalId", input.sourceSignalId);
	if (input.targetProfileId) searchParams.set("targetProfileId", input.targetProfileId);
	const query = searchParams.toString();
	return await client.requestJson({
		url: `${buildSpaceWakeTraceWaterfallPath(input.spaceId)}?${query}`,
		operation: "space.wake_trace_waterfall",
		method: "GET",
		requestContext: input.requestContext,
		signal: input.signal,
		...input.spacePassword === void 0 ? {} : { spacePassword: input.spacePassword },
		schema: getSpaceWakeTraceWaterfallResponseSchema,
		invalidMessage: "invalid wake trace waterfall response"
	});
}

//#endregion
//#region src/space/api.ts
const createSpaceApi = (client) => {
	return {
		createSpace: async (input) => await createSpace(client, input),
		deleteSpace: async (input) => await deleteSpace(client, input),
		addMembers: async (input) => await addSpaceMembers(client, input),
		setAccessMode: async (input) => await setSpaceAccessMode(client, input),
		leaveSpace: async (input) => await leaveSpace(client, input),
		getBootstrap: async (input) => await getSpaceBootstrap(client, input),
		getStatus: async (input) => await getSpaceStatus(client, input),
		getMembersSnapshot: async (input) => await getSpaceMembersSnapshot(client, input),
		getMemberDirectory: async (input) => await getSpaceMemberDirectory(client, input),
		getIdentityDirectory: async (input) => await getSpaceIdentityDirectory(client, input),
		getViewerMembership: async (input) => await getSpaceViewerMembership(client, input),
		listSignals: async (input) => await listSpaceSignals(client, input),
		listSignalEnvelopes: async (input) => await listSpaceSignalEnvelopes(client, input),
		getUpdates: async (input) => await getSpaceUpdates(client, input),
		getActiveWakeProgress: async (input) => await getActiveSpaceWakeProgress(client, input),
		postWakeStop: async (input) => await postSpaceWakeStop(client, input),
		getWakeProgress: async (input) => await getSpaceWakeProgress(client, input),
		getWakeTraceWaterfall: async (input) => await getSpaceWakeTraceWaterfall(client, input),
		getMessageDetails: async (input) => await getSpaceMessageDetails(client, input),
		createDiagnosticReport: async (input) => await createSpaceDiagnosticReport(client, input),
		postNormalMessage: async (input) => await postSpaceNormalMessage(client, input),
		createThread: async (input) => await createSpaceThread(client, input),
		listThreads: async (input) => await listSpaceThreads(client, input),
		getThread: async (input) => await getSpaceThread(client, input),
		listThreadEvents: async (input) => await listSpaceThreadEvents(client, input),
		listThreadSignals: async (input) => await listSpaceThreadSignals(client, input),
		postThreadSignal: async (input) => await postSpaceThreadSignal(client, input)
	};
};

//#endregion
//#region src/transport.ts
function createFetchTransport(input) {
	const defaultTimeoutMs = normalizeOptionalTimeoutMs(input.defaultTimeoutMs);
	return async function fetchTransport(request) {
		const timeoutMs = normalizeOptionalTimeoutMs(request.timeoutMs) ?? defaultTimeoutMs;
		const timeoutSignal = timeoutMs === void 0 ? null : AbortSignal.timeout(timeoutMs);
		const composed = composeAbortSignals(request.init?.signal, timeoutSignal);
		try {
			return await input.fetch(request.url, {
				...request.init,
				signal: composed.signal
			});
		} catch (error) {
			if (timeoutSignal?.aborted && timeoutMs !== void 0) throw createTimeoutError({
				operation: request.operation,
				timeoutMs,
				cause: error
			});
			if (shouldWrapNetworkRequestError(error)) throw createNetworkRequestError({
				operation: request.operation,
				url: request.url,
				cause: error
			});
			throw error;
		} finally {
			composed.cleanup();
		}
	};
}
function createTimeoutError(input) {
	const error = new Error(`${input.operation} request timed out after ${input.timeoutMs}ms`, { cause: input.cause });
	error.code = "network.timeout";
	return error;
}
function createNetworkRequestError(input) {
	const error = new Error(`${input.operation} request could not reach ${describeRequestTarget(input.url)} (${toErrorMessage(input.cause)})`, { cause: input.cause });
	error.code = "network.request_failed";
	return error;
}
function normalizeOptionalTimeoutMs(value) {
	if (value === void 0) return;
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) throw new Error("timeoutMs must be a finite positive number");
	return Math.trunc(value);
}
function describeRequestTarget(url) {
	try {
		return new URL(url).origin;
	} catch {
		return url;
	}
}
function toErrorMessage(error) {
	if (error instanceof Error) {
		const message = error.message.trim();
		return message.length > 0 ? message : "request failed";
	}
	const message = String(error ?? "").trim();
	return message.length > 0 ? message : "request failed";
}
function shouldWrapNetworkRequestError(error) {
	if (error instanceof TypeError) return true;
	const message = toErrorMessage(error).toLowerCase();
	return message.includes("fetch failed") || message.includes("failed to fetch") || message.includes("network request failed");
}
function composeAbortSignals(parent, timeout) {
	if (!(parent || timeout)) return {
		signal: void 0,
		cleanup: () => void 0
	};
	if (!parent) return {
		signal: timeout ?? void 0,
		cleanup: () => void 0
	};
	if (!timeout) return {
		signal: parent,
		cleanup: () => void 0
	};
	if (parent.aborted || timeout.aborted) {
		const controller = new AbortController();
		controller.abort();
		return {
			signal: controller.signal,
			cleanup: () => void 0
		};
	}
	const controller = new AbortController();
	const abort = () => {
		controller.abort();
	};
	parent.addEventListener("abort", abort, { once: true });
	timeout.addEventListener("abort", abort, { once: true });
	return {
		signal: controller.signal,
		cleanup: () => {
			parent.removeEventListener("abort", abort);
			timeout.removeEventListener("abort", abort);
		}
	};
}

//#endregion
export { WakeLoopApiClientHttpError, WakeLoopApiClientInvalidResponseError, buildAccountSpaceIndexPath, buildModelCatalogPath, buildProfileSpaceHistoryListPath, buildProfileSpaceHistoryStatusPath, buildProfileSpaceHistoryUpsertPath, buildSpaceBootstrapReadPath, createAccountSpaceIndexApi, createAgentProfileBindingApi, createConfigApi, createCurrentDeviceApi, createFetchTransport, createLocalRuntimeExpectedRoutesApi, createPrepareReadinessApi, createProfileSpaceHistoryApi, createRuntimeReportingApi, createSpaceApi, createWakeLoopApiClient, getModelCatalog, normalizeApiBaseUrl, publicSpaceUrls, resolveAccountSpaceIndexGatewayHost, resolveProfileSpaceHistoryGatewayHost, setSpaceAccessMode, toStructuredApiClientFailure };
//# sourceMappingURL=index.js.map